import { useMemo } from 'react';

import dayjs from 'dayjs';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  ZoomBoundary,
  ZoomProps,
  ZoomStateReturnType,
} from '@cast/design-system';

import { RangePickerQueryParamKeys } from '@cast/shared-date-ui';

import { INVENTORY_WORKLOADS_QUERY_PARAM_KEYS } from './useInventoryWorkloadsTimeRangeSelection';

type UseReactRouterZoomStrategyArgs = Pick<
  ZoomProps,
  'onZoom' | 'onResetZoom'
> & {
  queryParamsKeys: Omit<RangePickerQueryParamKeys, 'timeZone'>;
};

const toUnixTimestamp = (timestamp: ZoomBoundary): number =>
  typeof timestamp === 'string' ? dayjs(timestamp).unix() : timestamp;

const useInventoryWorkloadsChartZoomState = ({
  queryParamsKeys,
  onZoom,
  onResetZoom,
}: UseReactRouterZoomStrategyArgs): ZoomStateReturnType => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasZoomed = location.state?.hasZoomed;

  const persistZoomState = (
    fromTimestamp: ZoomBoundary,
    toTimestamp: ZoomBoundary
  ) => {
    setSearchParams(
      (prev) => {
        prev.set(
          queryParamsKeys.fromDate,
          dayjs.unix(toUnixTimestamp(fromTimestamp)).toISOString()
        );
        prev.set(
          queryParamsKeys.toDate,
          dayjs.unix(toUnixTimestamp(toTimestamp)).toISOString()
        );
        prev.delete(queryParamsKeys.dateRangePreset);
        return prev;
      },
      { replace: true, state: { hasZoomed: true } }
    );

    onZoom?.(fromTimestamp, toTimestamp);
  };

  const resetZoomState = () => {
    setSearchParams(
      (prev) => {
        prev.delete(queryParamsKeys.fromDate);
        prev.delete(queryParamsKeys.toDate);
        prev.delete(queryParamsKeys.dateRangePreset);
        return prev;
      },
      { replace: true }
    );

    onResetZoom?.();
  };

  const zoomedRange = useMemo(() => {
    const from = searchParams.get(queryParamsKeys.fromDate);
    const to = searchParams.get(queryParamsKeys.toDate);

    return {
      from: from ? toUnixTimestamp(from) : undefined,
      to: to ? toUnixTimestamp(to) : undefined,
    };
  }, [queryParamsKeys.fromDate, queryParamsKeys.toDate, searchParams]);

  return {
    zoomedRange,
    persistZoomState,
    hasZoomed,
    resetZoomState,
  };
};

const useBoundInventoryWorkloadsChartZoomState =
  useInventoryWorkloadsChartZoomState.bind(null, {
    queryParamsKeys: INVENTORY_WORKLOADS_QUERY_PARAM_KEYS,
  });

export const inventoryWorkloadsChartZoomProps: Pick<
  ZoomProps,
  'xAxisDataKey' | 'useZoomState'
> = {
  xAxisDataKey: 'chartTimestampUnix',
  useZoomState: useBoundInventoryWorkloadsChartZoomState,
};
